import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ShieldCheck,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowLeft,
  Share2,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { GovernmentDisclaimer } from '../components/GovernmentDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WaitlistStatusPage — public, email-free status view for a waitlist row.
 *
 * Keyed on an opaque `share_token` (no email in URL, response, or DOM) so
 * requesters can post `/waitlist/status/<token>` on social media
 * ("#47 on GhostComm's waitlist") without leaking their own address.
 *
 * Three states:
 *  - pending  → shows queue position + encouragement + share controls
 *  - approved → shows "a member is reaching out with an invite code"
 *  - rejected → neutral, generic copy (no reason leaked)
 *
 * Unknown tokens render a gentle 404 panel instead of a raw error.
 */

const fmtAge = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const WaitlistStatusPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, notFound: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/waitlist/status/${encodeURIComponent(token)}`);
        if (!cancelled) setState({ loading: false, data: res.data, notFound: false });
      } catch (err) {
        if (!cancelled) {
          setState({
            loading: false,
            data: null,
            notFound: err?.response?.status === 404,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const { loading, data, notFound } = state;
  // Share points at the crawlable `/api/share/...` endpoint so it unfurls
  // on Twitter/Discord/Slack/etc. Human visitors get meta-refreshed onto
  // this (/waitlist/status/:token) page.
  const statusUrl =
    typeof window !== 'undefined' && token
      ? `${window.location.origin}/api/share/waitlist/${token}`
      : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(statusUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Government Shield waitlist',
          text:
            data?.status === 'pending' && data?.position
              ? `I'm #${data.position} on the Government Shield waitlist.`
              : 'Follow my Government Shield waitlist status',
          url: statusUrl,
        });
      } catch {}
    } else {
      copyLink();
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2"
      style={{ backgroundColor: '#05050A' }}
    >
      {/* Visual Side — same branding as the submit page */}
      <div
        className="hidden lg:flex flex-col justify-center items-center relative overflow-hidden"
        style={{ backgroundColor: '#0F1115' }}
      >
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative z-10 p-12 text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-8 glow-primary">
            <ShieldCheck
              className="w-10 h-10 text-primary"
              strokeWidth={1.5}
            />
          </div>
          <h1
            className="text-4xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Government Shield
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            Your link to the queue. Share it — members see it too when they
            review requests.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col justify-center items-center p-6 lg:p-12">
        <Card className="w-full max-w-md bg-card/50 backdrop-blur border-border">
          <CardHeader>
            <CardTitle
              className="text-2xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Waitlist status
            </CardTitle>
            <CardDescription className="text-slate-400">
              Your peer-review queue position. Updates when anyone ahead of
              you is approved or rejected.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {loading && (
              <div
                className="flex items-center justify-center py-10"
                data-testid="waitlist-status-loading"
              >
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {!loading && notFound && (
              <div
                className="text-center py-8 space-y-3"
                data-testid="waitlist-status-notfound"
              >
                <AlertCircle className="w-10 h-10 text-amber-400 mx-auto opacity-80" />
                <p className="text-white font-medium">Link not found</p>
                <p className="text-sm text-slate-400">
                  This status link is invalid or has expired. Try submitting a
                  new request if you haven't already.
                </p>
                <Link to="/waitlist">
                  <Button variant="outline" className="mt-2">
                    Request access
                  </Button>
                </Link>
              </div>
            )}

            {!loading && data && data.status === 'pending' && (
              <div
                className="space-y-4 text-center"
                data-testid="waitlist-status-pending"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10">
                  <Clock className="w-7 h-7 text-primary" strokeWidth={1.5} />
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-400">Your position</p>
                  <p
                    className="text-5xl font-bold text-primary font-mono tracking-tight"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                    data-testid="waitlist-position-big"
                  >
                    #{data.position}
                  </p>
                  <p className="text-xs text-slate-500">
                    Submitted {fmtAge(data.created_at)}
                  </p>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">
                  An existing member will review pending requests. When yours
                  reaches the front, they'll reach out with an invite code
                  directly.
                </p>

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={share}
                    data-testid="waitlist-status-share"
                  >
                    <Share2 className="w-3.5 h-3.5 mr-2" />
                    Share
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={copyLink}
                    data-testid="waitlist-status-copy"
                  >
                    <Copy className="w-3.5 h-3.5 mr-2" />
                    Copy link
                  </Button>
                </div>
              </div>
            )}

            {!loading && data && data.status === 'approved' && (
              <div
                className="space-y-4 text-center"
                data-testid="waitlist-status-approved"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10">
                  <CheckCircle2
                    className="w-8 h-8 text-emerald-400"
                    strokeWidth={1.5}
                  />
                </div>
                <p className="text-white font-medium">You're approved</p>
                <p className="text-sm text-slate-400 leading-relaxed">
                  A current member has approved your request
                  {data.approved_at ? ` (${fmtAge(data.approved_at)})` : ''}{' '}
                  and will send you a single-use invite code directly. Check
                  the email you signed up with.
                </p>
              </div>
            )}

            {!loading && data && data.status === 'rejected' && (
              <div
                className="space-y-4 text-center"
                data-testid="waitlist-status-rejected"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-500/10">
                  <XCircle className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
                </div>
                <p className="text-white font-medium">Request closed</p>
                <p className="text-sm text-slate-400 leading-relaxed">
                  This request was not approved. Government Shield is invite-only by
                  design and members review requests individually — no reason
                  is shared, and new submissions from the same email aren't
                  accepted.
                </p>
              </div>
            )}

            <div className="pt-3 border-t border-border text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                data-testid="waitlist-status-signin"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
        <GovernmentDisclaimer className="mt-6 max-w-md w-full" />
      </div>
    </div>
  );
};

export default WaitlistStatusPage;
